import { adminHttp, aggregatorHttp, api } from './client'
import type {
  AdminAuthResponse,
  AdminLoginRequest,
  AdminUser,
  AdminUserCreateRequest,
  AdminUserUpdateRequest,
  AdminProviderResponse,
  AdminProviderUpdateRequest,
  BulkActionResponse,
  BulkApproveRequest,
  BulkAssignCategoryRequest,
  VerificationDecisionRequest,
  VerificationReviewResponse,
  VerificationStatus,
  AggregatorCreateRequest,
  AggregatorResponse,
  AggregatorVouchRequest,
  AddProfileRequest,
  AddRoleRequest,
  AuthResponse,
  GeocodeBackfillResponse,
  SignalsBackfillResponse,
  SignalsNetworkStatus,
  BlockCreateRequest,
  ConnectConsentResponse,
  ConnectionRequestCreateRequest,
  ConnectionRequestResponse,
  ConnectionRespondRequest,
  ContactRevealResponse,
  CustomNeedRequestCreateRequest,
  CustomNeedRequestResponse,
  EngagementCheckinRequest,
  EngagementCheckinResponse,
  EmailTemplatePreviewRequest,
  EmailTemplatePreviewResponse,
  AdminAnalyticsResponse,
  AdminDailyAnalytics,
  AdminCategoryAnalytics,
  EmailTemplateResponse,
  EmailTemplateUpdateRequest,
  ChildActivityResponse,
  ImportStreamLine,
  EngagementCreateRequest,
  EngagementResponse,
  InstitutionRegisterRequest,
  AggregatorOrgRegisterRequest,
  AggregatorOrgProfile,
  AggregatorAudience,
  CoordinatorInviteView,
  CoordinatorSignupRequest,
  OrgCoordinatorResponse,
  OrgInviteResponse,
  CoordinatorBulkInviteResponse,
  AggregatorPortalAuthResponse,
  AggregatorPortalProfile,
  AggregatorPersonResponse,
  AggregatorNetworkDashboard,
  AdminAggregatorResponse,
  AdminAggregatorOverview,
  GuardianChildCreateRequest,
  GuardianLinkCreateRequest,
  GuardianLinkInviteRequest,
  GuardianLinkRespondRequest,
  GuardianLinkResponse,
  GuardianLinkViewResponse,
  LearnerNeedRequest,
  LearnerNeedResponse,
  LoginRequest,
  MapLearnerSearchParams,
  MapLearnerSearchResponse,
  MapSearchParams,
  MapSearchResponse,
  ParticipantCardResponse,
  NdliHandoffResponse,
  PartnerHandoffResponse,
  FourCResultResponse,
  CodeSentResponse,
  EntityKind,
  BlockedPerson,
  AdminTrustSummary,
  AdminTrustReportRow,
  AdminTrustAccountRow,
  TrustReportStatus,
  VerifyRegistrationRequest,
  VerificationPhotoInfo,
  VerificationPhotosResponse,
  PersonaSummary,
  PocketCreateRequest,
  PocketResponse,
  ProfileResponse,
  ProfileUpdateRequest,
  ProviderServiceRequest,
  ProviderServiceResponse,
  RatingCreateRequest,
  RatingResponse,
  ReferralCreateRequest,
  ReferralResponse,
  RegisterRequest,
  TermsAcceptRequest,
  TermsVersionResponse,
  TrustReportCreateRequest,
  TrustReportResponse,
  TrustStatusResponse,
  TutorListingResponse,
  ImportInviteReport,
  ImportInviteStatus,
  ImportInviteView,
  DegreeStatus,
  VioletDotsStatus,
} from '../types/api'

/**
 * Signing up and signing in - no passwords: both are a 6-digit join code emailed to the account.
 * Sign up = register (or registerInstitution), then verifyRegistration. Sign in = requestLoginCode,
 * then login.
 */
export const authApi = {
  /** Step 1 of signing up: checks the form and emails the join code. No account exists yet. */
  register: (body: RegisterRequest) => api.post<CodeSentResponse>('/auth/register', body),
  /** Schools, colleges and companies - a different identity model, so a different endpoint. */
  registerInstitution: (body: InstitutionRegisterRequest) =>
    api.post<CodeSentResponse>('/auth/register/institution', body),
  /** Step 2 of signing up: the code creates the account and signs it in. */
  verifyRegistration: (body: VerifyRegistrationRequest) => api.post<AuthResponse>('/auth/register/verify', body),
  /**
   * Step 1 of signing in, by email only. 404 when the email has no account - send them to sign up;
   * 403 when it has no account for the role picked (no code is sent).
   */
  requestLoginCode: (email: string, role?: EntityKind) =>
    api.post<CodeSentResponse>('/auth/login/code', { email, role }),
  login: (body: LoginRequest) => api.post<AuthResponse>('/auth/login', body),
}

export const partnersApi = {
  /**
   * Where to send someone who pressed "Jobs / Internship".
   *
   * <p>POST, not GET: the first call registers them as a candidate on TalEvate, and every call
   * mints a single-use link that is spent the moment it opens - a GET would be fair game for a
   * prefetch that burns the link before the person clicks. The credentials that make this work
   * never leave the server.
   */
  talevateHandoff: () => api.post<PartnerHandoffResponse>('/partners/talevate/handoff', {}),
  /** Registers the participant with mySATHI (by mobile) and returns where the 4C button goes. */
  mysathiHandoff: () => api.post<PartnerHandoffResponse>('/partners/mysathi/handoff', {}),
  fourCResult: () => api.get<FourCResultResponse>('/partners/mysathi/4c-result'),
  /** Whether they may take AI Certification on Violet Dots, and their certificates so far (V52). */
  violetDots: () => api.get<VioletDotsStatus>('/partners/violetdots'),
  /** The signed link that signs them in on Violet Dots, with any answers the page had to ask for. */
  violetDotsHandoff: (body: { degreeStatus?: DegreeStatus; confirmsAdult?: boolean }) =>
    api.post<PartnerHandoffResponse>('/partners/violetdots/handoff', body),
  /** Public: Violet Dots' "Back to Yellow Dots" token, exchanged for a session (V53). */
  violetDotsReturn: (token: string) => api.post<AuthResponse>('/partners/violetdots/return', { token }),
  /**
   * Registers the participant with NDLI for the chosen exam stream and returns the MyZone sign-in
   * link with its token - both are needed together, so the caller POSTs them straight away.
   */
  ndliHandoff: (stream: string) =>
    api.post<NdliHandoffResponse>(`/partners/ndli/${stream.toLowerCase()}/handoff`, {}),
  /**
   * The same for "Fill College Applications": NDLI's myzone-registration with target EASY_APPLY,
   * for a course category (MBA, CUET, LAW, AFTER-12 or ENGG).
   */
  ndliEasyApplyHandoff: (stream: string) =>
    api.post<NdliHandoffResponse>(`/partners/ndli/easy-apply/${stream.toLowerCase()}/handoff`, {}),
  /** Whether the sidebar offers "SATHI NAO Assessment" to this student - see NdliService.isSathiNaoFor. */
  sathiNaoAvailability: () => api.get<{ available: boolean }>('/partners/ndli/sathi-nao'),
  /** "SATHI NAO Assessment": one NDLI sign-in, no choice to make first - see NdliService. */
  ndliSathiNaoHandoff: () => api.post<NdliHandoffResponse>('/partners/ndli/sathi-nao/handoff', {}),
}

export const profileApi = {
  /** A fresh token for this session - the website asks while the person is active (AuthContext). */
  refreshSession: () => api.post<AuthResponse>('/profile/session/refresh'),
  getOwn: () => api.get<ProfileResponse>('/profile'),
  updateOwn: (body: ProfileUpdateRequest) => api.patch<ProfileResponse>('/profile', body),
  /** Where to put me from my locality + pincode (the locality's centre, else the pincode's); undefined when neither resolves. */
  locationSuggestion: () => api.get<{ lat: number; lng: number } | undefined>('/profile/location-suggestion'),
  getCard: (participantId: string) => api.get<ParticipantCardResponse>(`/participants/${participantId}`),
  /** Opens this person's other side and returns a session on it, not on the one that asked. */
  addRole: (body: AddProfileRequest) => api.post<AuthResponse>('/profile/add-role', body),
  /** Moves the session to the caller's other profile. Returns the token for it. */
  switchTo: (profileId: string) => api.post<AuthResponse>('/profile/switch', { profileId }),
  /** Every role the caller's account holds - the header avatar switcher's dropdown contents. */
  listRoles: () => api.get<PersonaSummary[]>('/profile/roles'),
  /**
   * Adds a Student, Tutor or Parent/Guardian role to the caller's account and returns a session on
   * it - the avatar switcher's "+ Add" form. Separate from {@link addRole} above, which is the
   * older Student/Tutor pair endpoint.
   */
  addSwitchableRole: (body: AddRoleRequest) => api.post<AuthResponse>('/profile/roles', body),
}

export const needsApi = {
  listOwn: () => api.get<LearnerNeedResponse[]>('/needs'),
  create: (body: LearnerNeedRequest) => api.post<LearnerNeedResponse>('/needs', body),
  update: (id: string, body: LearnerNeedRequest) => api.put<LearnerNeedResponse>(`/needs/${id}`, body),
  remove: (id: string) => api.delete<void>(`/needs/${id}`),
}

export const customNeedsApi = {
  listOwn: () => api.get<CustomNeedRequestResponse[]>('/needs/custom'),
  create: (body: CustomNeedRequestCreateRequest) => api.post<CustomNeedRequestResponse>('/needs/custom', body),
}

export const servicesApi = {
  listOwn: () => api.get<ProviderServiceResponse[]>('/services'),
  upsert: (body: ProviderServiceRequest) => api.put<ProviderServiceResponse>('/services', body),
  remove: (id: string) => api.delete<void>(`/services/${id}`),
}

export const connectionsApi = {
  /** The contact-sharing statements to show before connecting or accepting. */
  consent: () => api.get<ConnectConsentResponse>('/connections/consent'),
  listOwn: () => api.get<ConnectionRequestResponse[]>('/connections'),
  create: (body: ConnectionRequestCreateRequest) => api.post<ConnectionRequestResponse>('/connections', body),
  respond: (id: string, body: ConnectionRespondRequest) =>
    api.post<ConnectionRequestResponse>(`/connections/${id}/respond`, body),
  getContact: (id: string) => api.get<ContactRevealResponse>(`/connections/${id}/contact`),
  /** Requests waiting on the caller as a parent, across all of their children. */
  guardianQueue: () => api.get<ConnectionRequestResponse[]>('/connections/guardian-queue'),
  /** A parent's answer on a child's behalf. Approving only moves it on to the other party. */
  guardianRespond: (id: string, body: ConnectionRespondRequest) =>
    api.post<ConnectionRequestResponse>(`/connections/${id}/guardian-respond`, body),
}

export const engagementsApi = {
  listOwn: () => api.get<EngagementResponse[]>('/engagements'),
  create: (body: EngagementCreateRequest) => api.post<EngagementResponse>('/engagements', body),
  checkin: (id: string, body: EngagementCheckinRequest) =>
    api.post<EngagementCheckinResponse>(`/engagements/${id}/checkin`, body),
  /** Ask to pick a lapsed engagement back up - or, if the other side already asked, accept it. */
  revive: (id: string) => api.post<EngagementResponse>(`/engagements/${id}/revive`, {}),
  /** The side that did not ask, accepting or declining. */
  respondToRevival: (id: string, accept: boolean) =>
    api.post<EngagementResponse>(`/engagements/${id}/revive/respond`, { accept }),
}

export const trustApi = {
  getStatus: () => api.get<TrustStatusResponse>('/trust/status'),
  listTerms: () => api.get<TermsVersionResponse[]>('/trust/terms'),
  acceptTerms: (body: TermsAcceptRequest) => api.post<void>('/trust/terms/accept', body),
  report: (body: TrustReportCreateRequest) => api.post<TrustReportResponse>('/trust/report', body),
  block: (body: BlockCreateRequest) => api.post<void>('/trust/block', body),
  unblock: (blockedParticipantId: string) => api.delete<void>(`/trust/block/${blockedParticipantId}`),
  /** Everyone the signed-in person has blocked, newest first. */
  listBlocks: () => api.get<BlockedPerson[]>('/trust/blocks'),
}

export const ratingsApi = {
  create: (body: RatingCreateRequest) => api.post<RatingResponse>('/ratings', body),
  listForParticipant: (participantId: string) => api.get<RatingResponse[]>(`/ratings/${participantId}`),
}

export const guardianApi = {
  linkChild: (body: GuardianLinkCreateRequest) => api.post<GuardianLinkResponse>('/guardian/children', body),
  listChildren: () => api.get<GuardianLinkResponse[]>('/guardian/children'),
  createChildAccount: (body: GuardianChildCreateRequest) =>
    api.post<GuardianLinkViewResponse>('/guardian/children/new', body),
  revoke: (childId: string) => api.post<void>(`/guardian/children/${childId}/revoke`),
  /** Every link the caller is part of, as either side. */
  listMyLinks: () => api.get<GuardianLinkViewResponse[]>('/guardian/links'),
  inviteGuardian: (body: GuardianLinkInviteRequest) =>
    api.post<GuardianLinkViewResponse>('/guardian/parents', body),
  respond: (linkId: string, body: GuardianLinkRespondRequest) =>
    api.post<GuardianLinkViewResponse>(`/guardian/links/${linkId}/respond`, body),
  /**
   * One child's connection trail: everyone they are connected to, and how each came about.
   *
   * <p>`days` windows the event list only - connections are always returned in full, because a
   * link made a year ago that is still live is the row a parent most needs to see.
   */
  childActivity: (childId: string, days?: number) =>
    api.get<ChildActivityResponse>(
      `/guardian/children/${childId}/activity${days ? `?days=${days}` : ''}`,
    ),
}

export const aggregatorsApi = {
  create: (body: AggregatorCreateRequest) => api.post<AggregatorResponse>('/aggregators', body),
  get: (id: string) => api.get<AggregatorResponse>(`/aggregators/${id}`),
  listAll: () => api.get<AggregatorResponse[]>('/aggregators'),
  vouch: (id: string, body: AggregatorVouchRequest) => api.post<void>(`/aggregators/${id}/vouch`, body),
}

/** Who a bulk upload or a people list is about. */
export type AggregatorPeopleKind = 'students' | 'tutors'

/**
 * The aggregator portal. Signed in with the aggregator token, which opens these and nothing else; no
 * aggregator id in any path - the server takes it from the session.
 */
/** Query string for the network dashboard, dropping what is unset. */
function dashboardQuery(params: { page?: number; limit?: number; status?: string; refresh?: boolean }): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== '') qs.set(key, String(value))
  return qs.toString()
}

export const aggregatorPortalApi = {
  /** An organisation signing up, step 1: emails a join code. Coordinators join by invitation - see acceptInvite. */
  register: (body: AggregatorOrgRegisterRequest) =>
    aggregatorHttp.post<CodeSentResponse>('/aggregator-portal/auth/register', body),
  /** Step 2: the code creates the organisation and signs it in. */
  verifyRegistration: (email: string, code: string) =>
    aggregatorHttp.post<AggregatorPortalAuthResponse>('/aggregator-portal/auth/register/verify', { email, code }),
  /** Sign-in step 1. Same answer whether or not the account exists. */
  requestLoginCode: (email: string) =>
    aggregatorHttp.post<CodeSentResponse>('/aggregator-portal/auth/login/code', { email }),
  /** Sign-in step 2. Organisations and coordinators alike; the reply's accountType says which. */
  login: (email: string, code: string) =>
    aggregatorHttp.post<AggregatorPortalAuthResponse>('/aggregator-portal/auth/login', { email, code }),
  viewInvite: (token: string) =>
    api.get<CoordinatorInviteView>(`/aggregator-portal/invites/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, body: CoordinatorSignupRequest) =>
    aggregatorHttp.post<AggregatorPortalAuthResponse>(`/aggregator-portal/invites/${encodeURIComponent(token)}/accept`, body),
  /** Public: the coordinator an onboarding link belongs to, and whom it brings in. */
  onboardingName: (code: string) =>
    api.get<{ name: string; audience?: AggregatorAudience }>(`/aggregator-portal/onboarding/${encodeURIComponent(code)}`),
  me: () => aggregatorHttp.get<AggregatorPortalProfile>('/aggregator-portal/me'),
  people: (kind: AggregatorPeopleKind) =>
    aggregatorHttp.get<AggregatorPersonResponse[]>(
      `/aggregator-portal/people?role=${kind === 'students' ? 'STUDENT' : 'TUTOR'}`,
    ),
  networkDashboard: (params: { page?: number; limit?: number; status?: string; refresh?: boolean } = {}) =>
    aggregatorHttp.get<AggregatorNetworkDashboard>(`/aggregator-portal/network-dashboard?${dashboardQuery(params)}`),
  networkExport: (status?: string) =>
    aggregatorHttp.download(`/aggregator-portal/network-dashboard/export${status ? `?status=${status}` : ''}`),
  /** Who this coordinator uploaded, and whether each was emailed and answered. */
  invitations: (kind: AggregatorPeopleKind, unsentOnly: boolean, page: number) =>
    aggregatorHttp.get<ImportInviteReport>(
      `/aggregator-portal/${kind}/invitations?page=${page}&size=50${unsentOnly ? '&unsentOnly=true' : ''}`,
    ),
  /** Emails the ticked invitations - an upload sends none by itself. */
  sendInvitations: (ids: string[]) =>
    aggregatorHttp.post<{ sent: number; skipped: number }>('/aggregator-portal/invitations/send', { ids }),
  importPeople: (
    kind: AggregatorPeopleKind,
    file: File,
    dryRun: boolean,
    onLine: (line: ImportStreamLine) => void,
  ) => {
    const form = new FormData()
    form.append('file', file)
    return aggregatorHttp.uploadStreaming<ImportStreamLine>(`/aggregator-portal/${kind}/import?dryRun=${dryRun}`, form, onLine)
  },
  importTemplate: (kind: AggregatorPeopleKind) => aggregatorHttp.download(`/aggregator-portal/${kind}/import/template`),
}

/** The organisation's half of the portal: invite coordinators, track everything they bring in. */
export const aggregatorOrgApi = {
  me: () => aggregatorHttp.get<AggregatorOrgProfile>('/aggregator-portal/org/me'),
  coordinators: () => aggregatorHttp.get<OrgCoordinatorResponse[]>('/aggregator-portal/org/coordinators'),
  invites: () => aggregatorHttp.get<OrgInviteResponse[]>('/aggregator-portal/org/invites'),
  invite: (email: string, contactName?: string) =>
    aggregatorHttp.post<OrgInviteResponse>('/aggregator-portal/org/invites', { email, contactName }),
  /** Invites every coordinator in a CSV or Excel file (columns email, name). */
  inviteAll: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return aggregatorHttp.upload<CoordinatorBulkInviteResponse>('/aggregator-portal/org/invites/import', form)
  },
  inviteTemplate: () => aggregatorHttp.download('/aggregator-portal/org/invites/import/template'),
  people: (coordinatorId: string) =>
    aggregatorHttp.get<AggregatorPersonResponse[]>(`/aggregator-portal/org/coordinators/${coordinatorId}/people`),
  networkDashboard: (coordinatorId: string, params: { page?: number; limit?: number; status?: string; refresh?: boolean } = {}) =>
    aggregatorHttp.get<AggregatorNetworkDashboard>(
      `/aggregator-portal/org/coordinators/${coordinatorId}/network-dashboard?${dashboardQuery(params)}`,
    ),
  networkExport: (coordinatorId: string, status?: string) =>
    aggregatorHttp.download(
      `/aggregator-portal/org/coordinators/${coordinatorId}/network-dashboard/export${status ? `?status=${status}` : ''}`,
    ),
}

export const pocketsApi = {
  create: (body: PocketCreateRequest) => api.post<PocketResponse>('/pockets', body),
  listActive: () => api.get<PocketResponse[]>('/pockets'),
}

export const referralsApi = {
  create: (body: ReferralCreateRequest) => api.post<ReferralResponse>('/referrals', body),
  listOwn: () => api.get<ReferralResponse[]>('/referrals/mine'),
  getByCode: (code: string) => api.get<ReferralResponse>(`/referrals/${code}`),
}

export const discoveryApi = {
  /** Takes an AbortSignal: map filters change faster than the network answers. */
  providers: (params: MapSearchParams, signal?: AbortSignal) => {
    const qs = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') qs.set(key, String(value))
    }
    const query = qs.toString()
    return api.get<MapSearchResponse>(`/discovery/providers${query ? `?${query}` : ''}`, signal)
  },
  learners: (params: MapLearnerSearchParams, signal?: AbortSignal) => {
    const qs = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') qs.set(key, String(value))
    }
    const query = qs.toString()
    return api.get<MapLearnerSearchResponse>(`/discovery/learners${query ? `?${query}` : ''}`, signal)
  },
}

export const tutorListingsApi = {
  optIn: () => api.post<TutorListingResponse>('/tutor-listings'),
  listLive: () => api.get<TutorListingResponse[]>('/tutor-listings'),
  getOwn: () => api.get<TutorListingResponse>('/tutor-listings/me'),
}

/** A tutor's, school's or college's own verification photos - 3 to 5, seen by the Yellow Dot team. */
export const verificationPhotosApi = {
  list: () => api.get<VerificationPhotosResponse>('/verification-photos'),
  upload: (file: Blob, name: string) => {
    const form = new FormData()
    form.append('file', file, name)
    return api.upload<VerificationPhotoInfo>('/verification-photos', form)
  },
  remove: (photoId: string) => api.delete<void>(`/verification-photos/${photoId}`),
  image: (photoId: string) => api.download(`/verification-photos/${photoId}`),
}

export const adminApi = {
  /** Ops sign-in step 1: emails a join code. Same answer whether or not the account exists. */
  requestLoginCode: (email: string) => adminHttp.post<CodeSentResponse>('/admin/auth/login/code', { email }),
  login: (body: AdminLoginRequest) => adminHttp.post<AdminAuthResponse>('/admin/auth/login', body),
  me: () => adminHttp.get<AdminUser>('/admin/me'),
  // Ops accounts and their roles - super admin only.
  listAdmins: () => adminHttp.get<AdminUser[]>('/admin/users'),
  createAdmin: (body: AdminUserCreateRequest) => adminHttp.post<AdminUser>('/admin/users', body),
  updateAdmin: (id: string, body: AdminUserUpdateRequest) => adminHttp.patch<AdminUser>(`/admin/users/${id}`, body),
  /**
   * Omit `status` for the live queue (everyone still waiting to be reviewed); `rejected` lists the
   * providers an admin turned down instead.
   */
  queue: (status?: VerificationStatus, rejected = false) =>
    adminHttp.get<VerificationReviewResponse[]>(
      rejected ? '/admin/verifications?rejected=true' : `/admin/verifications${status ? `?status=${status}` : ''}`,
    ),
  /** One verification photo's image - fetched with the token, so not a plain <img src>. */
  // Reports and blocks - super admin only.
  trustSummary: () => adminHttp.get<AdminTrustSummary>('/admin/trust/summary'),
  trustReports: (status?: TrustReportStatus) =>
    adminHttp.get<AdminTrustReportRow[]>(`/admin/trust/reports${status ? `?status=${status}` : ''}`),
  trustAccounts: () => adminHttp.get<AdminTrustAccountRow[]>('/admin/trust/accounts'),
  updateTrustReport: (id: string, body: { status?: TrustReportStatus; actionTaken?: string }) =>
    adminHttp.patch<AdminTrustReportRow>(`/admin/trust/reports/${id}`, body),
  verificationPhoto: (participantId: string, photoId: string) =>
    adminHttp.download(`/admin/verifications/${participantId}/photos/${photoId}`),
  approve: (participantId: string, body: VerificationDecisionRequest) =>
    adminHttp.post<VerificationReviewResponse>(
      `/admin/verifications/${participantId}/approve`,
      body,
    ),
  reject: (participantId: string, body: VerificationDecisionRequest) =>
    adminHttp.post<VerificationReviewResponse>(
      `/admin/verifications/${participantId}/reject`,
      body,
    ),

  /** The providers directory: every provider, filterable. Omitted params are not sent at all. */
  providers: (filters: {
    city?: string
    state?: string
    category?: string
    subCategory?: string
    status?: VerificationStatus
    /** Ignores `category` - finds only providers with none recorded at all. */
    categoryUnassigned?: boolean
    /** Ignores `subCategory` - finds only providers with none recorded at all. */
    subCategoryUnassigned?: boolean
  }) => {
    const params = new URLSearchParams()
    if (filters.city) params.set('city', filters.city)
    if (filters.state) params.set('state', filters.state)
    if (filters.categoryUnassigned) params.set('categoryUnassigned', 'true')
    else if (filters.category) params.set('category', filters.category)
    if (filters.subCategoryUnassigned) params.set('subCategoryUnassigned', 'true')
    else if (filters.subCategory) params.set('subCategory', filters.subCategory)
    if (filters.status) params.set('status', filters.status)
    const qs = params.toString()
    return adminHttp.get<AdminProviderResponse[]>(`/admin/providers${qs ? `?${qs}` : ''}`)
  },
  updateProvider: (participantId: string, body: AdminProviderUpdateRequest) =>
    adminHttp.patch<AdminProviderResponse>(`/admin/providers/${participantId}`, body),
  bulkApprove: (body: BulkApproveRequest) =>
    adminHttp.post<BulkActionResponse>('/admin/providers/bulk-approve', body),
  /** Suspends every listed provider - no dot on the map, no connections either direction. */
  /** Reinstates suspended providers and republishes them to the network; refuses anyone not suspended. */
  bulkActivate: (participantIds: string[]) =>
    adminHttp.post<BulkActionResponse>('/admin/providers/bulk-activate', { participantIds }),
  bulkDeactivate: (participantIds: string[]) =>
    adminHttp.post<BulkActionResponse>('/admin/providers/bulk-deactivate', { participantIds }),
  bulkAssignCategory: (body: BulkAssignCategoryRequest) =>
    adminHttp.post<BulkActionResponse>('/admin/providers/bulk-assign-category', body),
  /** Recomputes every provider's Category/Sub Category columns from their real service rows. */
  reprojectProviders: () => adminHttp.post<BulkActionResponse>('/admin/providers/reproject'),

  /**
   * Email templates. List and update only - the catalogue is whatever the backend sends, so there
   * is no create or delete.
   */
  /** The Analysis tab - totals and online-now per role. */
  analytics: () => adminHttp.get<AdminAnalyticsResponse>('/admin/analytics'),
  /** Registrations per day over the last `days` days - the three sign-up charts. */
  analyticsDaily: (days: number) => adminHttp.get<AdminDailyAnalytics>(`/admin/analytics/daily?days=${days}`),
  /** Needs vs. services by taxonomy category - the category-mix panels. */
  analyticsCategories: () => adminHttp.get<AdminCategoryAnalytics>('/admin/analytics/categories'),
  emailTemplates: () => adminHttp.get<EmailTemplateResponse[]>('/admin/email-templates'),
  updateEmailTemplate: (id: string, body: EmailTemplateUpdateRequest) =>
    adminHttp.put<EmailTemplateResponse>(`/admin/email-templates/${id}`, body),
  previewEmailTemplate: (id: string, body: EmailTemplatePreviewRequest) =>
    adminHttp.post<EmailTemplatePreviewResponse>(`/admin/email-templates/${id}/preview`, body),

  /**
   * Bulk student sign-up from a CSV, streamed: `onLine` is called once with the row count, then
   * once per row as it is processed, so a caller can show real progress on a large file instead of
   * waiting on one response at the end - see `NdjsonWriter` on the backend.
   *
   * <p>`dryRun` writes nothing and reports what would happen - the pass to run first, so the typos
   * turn up before the accounts exist. `sendInvites` emails every created account its invitation
   * at once, instead of leaving them for the Report status tab.
   */
  importStudents: (file: File, dryRun: boolean, onLine: (line: ImportStreamLine) => void, sendInvites = false) => {
    const form = new FormData()
    form.append('file', file)
    return adminHttp.uploadStreaming<ImportStreamLine>(
      `/admin/students/import?dryRun=${dryRun}&sendInvites=${sendInvites}`,
      form,
      onLine,
    )
  },
  /** The blank CSV, fetched rather than linked because the endpoint needs the ops token. */
  studentImportTemplate: () => adminHttp.download('/admin/students/import/template'),
  /**
   * The tutor side of the same import. A separate path rather than a role parameter, so a
   * mistyped query string cannot file a file of tutors as learners.
   */
  importTutors: (file: File, dryRun: boolean, onLine: (line: ImportStreamLine) => void, sendInvites = false) => {
    const form = new FormData()
    form.append('file', file)
    return adminHttp.uploadStreaming<ImportStreamLine>(
      `/admin/tutors/import?dryRun=${dryRun}&sendInvites=${sendInvites}`,
      form,
      onLine,
    )
  },
  tutorImportTemplate: () => adminHttp.download('/admin/tutors/import/template'),
  /** The bulk upload report: everyone uploaded, and whether they confirmed, rejected or not answered (V51). */
  importInvites: (status: ImportInviteStatus | undefined, unsentOnly: boolean, page: number) =>
    adminHttp.get<ImportInviteReport>(
      `/admin/import-invites?page=${page}&size=50${status ? `&status=${status}` : ''}${unsentOnly ? '&unsentOnly=true' : ''}`,
    ),
  /** Emails the ticked invitations - the only way an upload's invitation goes out. */
  sendImportInvites: (ids: string[]) =>
    adminHttp.post<{ sent: number; skipped: number }>('/admin/import-invites/send', { ids }),
  /** Aggregators that signed up through the portal. */
  listAggregators: () => adminHttp.get<AdminAggregatorResponse[]>('/admin/aggregators'),
  /** Organisations, their coordinators, and the learners and tutors each brought in. */
  aggregatorOverview: () => adminHttp.get<AdminAggregatorOverview>('/admin/aggregators/overview'),
  suspendAggregatorOrg: (id: string) => adminHttp.post<void>(`/admin/aggregators/orgs/${id}/suspend`),
  reinstateAggregatorOrg: (id: string) => adminHttp.post<void>(`/admin/aggregators/orgs/${id}/reinstate`),
  /** Approves a pending partner organisation; the applicant is emailed (V56). */
  approveAggregatorOrg: (id: string) => adminHttp.post<void>(`/admin/aggregators/orgs/${id}/approve`),
  /** Re-runs an approved organisation's aggregator-dpg registration, in the background. */
  retryAggregatorOrgDpg: (id: string) => adminHttp.post<void>(`/admin/aggregators/orgs/${id}/dpg-retry`),
  /** Rejects a pending partner organisation; the reason is emailed to the applicant (V56). */
  rejectAggregatorOrg: (id: string, reason: string) =>
    adminHttp.post<void>(`/admin/aggregators/orgs/${id}/reject`, { reason }),
  /** Reinstates a suspended aggregator (they are approved on sign-up). */
  approveAggregator: (id: string) => adminHttp.post<AdminAggregatorResponse>(`/admin/aggregators/${id}/approve`),
  suspendAggregator: (id: string) => adminHttp.post<AdminAggregatorResponse>(`/admin/aggregators/${id}/suspend`),
  connectAggregator: (id: string) =>
    adminHttp.post<AdminAggregatorResponse>(`/admin/aggregators/${id}/connect-network`),
  /**
   * Places one batch of accounts that have a pincode but no dot. Slow on purpose - the lookups
   * are spaced a second apart - and safe to repeat: a placed account is not re-examined.
   *
   * <p>Deliberately not wired to a button. An import geocodes everyone it creates, so there is
   * normally nothing for this to do, and a control that says "you do not need this" is a control
   * that should not be on the page. It stays because the import is tolerant by design - a row
   * whose lookup fails is still created, without a dot - and if that ever happens in bulk this is
   * how those accounts are reached. Re-adding the card is one line.
   */
  geocodeBackfill: () => adminHttp.post<GeocodeBackfillResponse>('/admin/geocode/backfill', {}),
  /**
   * One slice of publishing people to the Signals-DPG network - those not fully on it yet. Carry on
   * with `after` = the previous `lastAttempted` until a slice tries fewer people than `limit`; that is
   * one forward pass, so people who cannot be published (no location, no service or need saved, a phone
   * the network refuses) are tried once and do not block the ones behind them.
   */
  signalsBackfill: (params: {
    limit: number
    providersOnly?: boolean
    learnersOnly?: boolean
    adultsOnly?: boolean
    /** A cursor: the previous slice's `lastAttempted`, for one forward pass through everyone. */
    after?: string
  }) => {
    const qs = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) if (value !== undefined) qs.set(key, String(value))
    return adminHttp.post<SignalsBackfillResponse>(`/admin/signals-dpg/backfill?${qs}`, {})
  },
  /** How many providers and seekers are on Signals-DPG, and how many are not yet. */
  signalsStatus: () => adminHttp.get<SignalsNetworkStatus>('/admin/signals-dpg/status'),
}

/**
 * A bulk-uploaded person's invitation (V51), opened from their email before they can sign in -
 * the token in the link is the credential.
 */
export const invitationApi = {
  view: (token: string) => api.get<ImportInviteView>(`/invitations/${encodeURIComponent(token)}`),
  confirm: (token: string) => api.post<ImportInviteView>(`/invitations/${encodeURIComponent(token)}/confirm`),
  reject: (token: string) => api.post<ImportInviteView>(`/invitations/${encodeURIComponent(token)}/reject`),
}

/** One aggregator-dpg connection - the portal's, or the campaign one (their routes need separate clients). */
export interface DpgConnection {
  purpose: 'PORTAL' | 'CAMPAIGN'
  /** Set up on this server (the Keycloak client exists) - otherwise nothing can be connected. */
  configured: boolean
  connected: boolean
  dpgAggregatorId: string | null
  /** aggregator-dpg's approval of the registration - most of its routes need "approved". */
  decision: string | null
  connectedAt: string | null
}

const DPG = '/aggregator-portal/dpg'

/**
 * aggregator-dpg (Blue Dots), through our backend as the signed-in coordinator - see
 * AggregatorDpgService. `call` paths are aggregator-dpg's own, e.g. '/v1/links'.
 */
export const aggregatorDpgApi = {
  status: () => aggregatorHttp.get<DpgConnection[]>(`${DPG}/status`),
  connect: (purpose: DpgConnection['purpose']) =>
    aggregatorHttp.post<{ url: string }>(`${DPG}/connect?purpose=${purpose}`),
  disconnect: (purpose: DpgConnection['purpose']) =>
    aggregatorHttp.delete<void>(`${DPG}/connection?purpose=${purpose}`),
  get: <T>(path: string) => aggregatorHttp.get<T>(`${DPG}/api${path}`),
  post: <T>(path: string, body?: unknown) => aggregatorHttp.post<T>(`${DPG}/api${path}`, body),
  patch: <T>(path: string, body?: unknown) => aggregatorHttp.patch<T>(`${DPG}/api${path}`, body),
  /** A CSV or other file from aggregator-dpg (dashboard export, upload template). */
  download: (path: string) => aggregatorHttp.download(`${DPG}/api${path}`),
  /** A CSV of participants, uploaded and started in one go; answers with the upload to poll. */
  bulkUpload: (participantType: 'seeker' | 'provider', file: File) => {
    const form = new FormData()
    form.append('participantType', participantType)
    form.append('file', file)
    return aggregatorHttp.upload<DpgBulkUpload>(`${DPG}/bulk-upload`, form)
  },
}

/** An aggregator-dpg bulk upload, as its API describes one. */
export interface DpgBulkUpload {
  upload_id: string
  status: string
  status_reason?: string | null
  participant_type: string
  total_rows?: number | null
  passed?: number | null
  failed?: number | null
  skipped?: number | null
  created_at: string
  completed_at?: string | null
}
